'use client';

/**
 * Business logic for the consent banner.
 *
 * The OneTrust SDK is the source of truth for consent state and banner visibility.
 * This hook renders a custom UI in place of the SDK's banner by:
 * 1. Hiding the SDK's banner via CSS
 * 2. Showing our banner when the SDK would show theirs (no bannerDismissedCookie)
 * 3. Delegating all consent actions to the SDK (AllowAll/RejectAll/ToggleInfoDisplay)
 * 4. Updating GCM immediately for the period before the SDK processes
 */
import { c as _c } from "react/compiler-runtime";
import { getCookie } from 'cookies-next/client';
import { useCallback, useEffect, useState } from 'react';
import { getAWSSignedRequestHeader } from '@sola/shared-utils/httpClient.ts';
import { type ConsentBannerData, oneTrustConfig } from '../../lib/consent-banner-data.ts';
import { ONETRUST_CATEGORIES } from '../../lib/consent-engine.ts';
import { gtag } from '../../lib/consent-gtag.ts';
import { openPreferenceCenter } from '../../lib/open-preference-center.ts';

/**
 * First-party cookie tracking explicit user interaction with the custom banner.
 * Separate from OptanonAlertBoxClosed which Close() sets on SDK load.
 */
const bannerDismissedCookie = 'ConsentBannerDismissed';

/** Mark the banner as dismissed via a domain-scoped cookie. */
const markBannerDismissed = (): void => {
  // eslint-disable-next-line unicorn/no-document-cookie
  document.cookie = `${bannerDismissedCookie}=1;path=/;max-age=${365 * 24 * 60 * 60};samesite=lax;domain=${oneTrustConfig.cookieDomain}`;
};

/**
 * POST consent decision to our server route for OneTrust's audit trail.
 * Fire-and-forget — failures are logged but don't block the UX.
 */
const logConsentToOneTrust = (interactionType: 'BANNER_ALLOW_ALL' | 'BANNER_REJECT_ALL', testMode: boolean): void => {
  const consentString = getCookie('OptanonConsentString');
  const payload = {
    interactionType,
    testMode,
    consentString: consentString || undefined
  };
  const send = async () => {
    // CloudFront OAC signs origin requests to the Next server's Lambda function
    // URL with SigV4, and Lambda rejects unsigned payloads. Without the body
    // hash in `x-amz-content-sha256`, this POST is rejected with a 403 at the
    // edge before the route handler runs. The hash must cover the exact bytes
    // sent, so both derive from the same `payload`.
    const signedHeader = await getAWSSignedRequestHeader(payload);
    await fetch('/api/consent/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...signedHeader
      },
      body: JSON.stringify(payload),
      keepalive: true
    });
  };
  send().catch(error => {
    console.error('[consent-banner] Failed to log consent:', error);
  });
};

/** Return type of the useConsentBanner hook */
export interface ConsentBannerActions {
  visible: boolean;
  sdkReady: boolean;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onShowPreferences: () => void;
}

/**
 * Hook that encapsulates all consent banner logic.
 * Uses the OneTrust SDK as the source of truth for visibility and consent state.
 */
export const useConsentBanner = (bannerData, t0) => {
  const $ = _c(18);
  const testMode = t0 === undefined ? false : t0;
  const [visible, setVisible] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setVisible(false);
      markBannerDismissed();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const dismiss = t1;
  let t2;
  let t3;
  if ($[1] !== bannerData.showBanner) {
    t2 = () => {
      const dismissed = Boolean(getCookie(bannerDismissedCookie));
      if (!dismissed && bannerData.showBanner) {
        setVisible(true);
      }
      let delayTimer;
      const interval = setInterval(() => {
        if (globalThis.OneTrust?.ToggleInfoDisplay) {
          globalThis.OneTrust.Close();
          delayTimer = setTimeout(() => setSdkReady(true), 500);
          clearInterval(interval);
        }
      }, 200);
      return () => {
        clearInterval(interval);
        clearTimeout(delayTimer);
      };
    };
    t3 = [bannerData.showBanner];
    $[1] = bannerData.showBanner;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const handleOpenPreferences = () => dismiss();
      globalThis.addEventListener("consent:open-preferences", handleOpenPreferences);
      return () => globalThis.removeEventListener("consent:open-preferences", handleOpenPreferences);
    };
    t5 = [dismiss];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== bannerData.showBanner) {
    t6 = () => {
      globalThis.dataLayer = globalThis.dataLayer || [];
      const hasInteracted = Boolean(getCookie("OptanonAlertBoxClosed"));
      if (!hasInteracted) {
        gtag("consent", "default", {
          analytics_storage: bannerData.showBanner ? "denied" : "granted",
          ad_storage: bannerData.showBanner ? "denied" : "granted",
          ad_user_data: bannerData.showBanner ? "denied" : "granted",
          ad_personalization: bannerData.showBanner ? "denied" : "granted",
          functionality_storage: "granted",
          personalization_storage: "granted",
          security_storage: "granted"
        });
      }
    };
    t7 = [bannerData.showBanner];
    $[6] = bannerData.showBanner;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] !== testMode) {
    t8 = () => {
      dismiss();
      gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted"
      });
      globalThis.OnetrustActiveGroups = `,${ONETRUST_CATEGORIES.STRICTLY_NECESSARY},${ONETRUST_CATEGORIES.PERFORMANCE},${ONETRUST_CATEGORIES.FUNCTIONAL},${ONETRUST_CATEGORIES.TARGETING},`;
      globalThis.OneTrust?.AllowAll();
      logConsentToOneTrust("BANNER_ALLOW_ALL", testMode);
    };
    $[9] = testMode;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const onAcceptAll = t8;
  let t9;
  if ($[11] !== testMode) {
    t9 = () => {
      dismiss();
      gtag("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied"
      });
      globalThis.OnetrustActiveGroups = `,${ONETRUST_CATEGORIES.STRICTLY_NECESSARY},`;
      globalThis.OneTrust?.RejectAll();
      logConsentToOneTrust("BANNER_REJECT_ALL", testMode);
    };
    $[11] = testMode;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const onRejectAll = t9;
  const onShowPreferences = _temp;
  let t10;
  if ($[13] !== onAcceptAll || $[14] !== onRejectAll || $[15] !== sdkReady || $[16] !== visible) {
    t10 = {
      visible,
      sdkReady,
      onAcceptAll,
      onRejectAll,
      onShowPreferences
    };
    $[13] = onAcceptAll;
    $[14] = onRejectAll;
    $[15] = sdkReady;
    $[16] = visible;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
};
function _temp() {
  openPreferenceCenter();
}